import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { sendPasswordResetEmail } from '../application/authPassword';
import {
  normalizeEmail,
  validatePasswordResetInput,
  type AuthFormValidationResult,
} from '../application/authFormValidation';
import { showInfo, showError } from '../../shared/ui/appToast';

function applyValidationFailure(result: Extract<AuthFormValidationResult, { ok: false }>) {
  if (result.toast.kind === 'info') {
    showInfo(result.toast.title, result.toast.body);
  } else {
    showError(result.toast.title, result.toast.body);
  }
}

type Options = {
  email: string;
};

/**
 * Hook de recuperación de contraseña desde el login: valida el correo,
 * avisa en nativo que el enlace abre el navegador y dispara
 * `sendPasswordResetEmail`. Mantiene el estado local `resetSending`.
 */
export function usePasswordResetFromLogin({ email }: Options) {
  const [resetSending, setResetSending] = useState(false);

  const handleReset = useCallback(async () => {
    const result = validatePasswordResetInput(email);
    if (!result.ok) {
      applyValidationFailure(result);
      return;
    }
    if (Platform.OS !== 'web') {
      showInfo(
        'Te abrimos el navegador',
        'Te mandamos un mail. Abrí el enlace en el navegador para cambiar la clave (en la app nativa no se cambia adentro).',
      );
    }
    setResetSending(true);
    try {
      await sendPasswordResetEmail(normalizeEmail(email));
    } finally {
      setResetSending(false);
    }
  }, [email]);

  return { handleReset, resetSending };
}
